/**
 * CategoryBrowser Component
 * Sidebar for filtering resources by category
 * UI based on prototypes/figma-bquest-app/src/components/Resources.tsx
 */

import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Filter } from 'lucide-react';
import { ResourceCategory } from '../types';

type CategoryOption = {
  value: ResourceCategory | 'ALL';
  label: string;
};

type CategoryBrowserProps = {
  selectedCategory: ResourceCategory | 'ALL';
  onCategoryChange: (category: ResourceCategory | 'ALL') => void;
};

const categories: CategoryOption[] = [
  { value: 'ALL', label: 'All Topics' },
  { value: ResourceCategory.AGING_IN_PLACE, label: 'Aging in Place' },
  { value: ResourceCategory.HOME_TRANSITION, label: 'Home Transition' },
  { value: ResourceCategory.END_OF_LIFE_CARE, label: 'End of Life Care' },
  { value: ResourceCategory.AFTER_LOSS_SUPPORT, label: 'After Loss Support' },
  { value: ResourceCategory.ADVISOR_RESOURCES, label: 'Advisor Resources' },
];

const popularTags = [
  'Dementia',
  'Finances',
  'Medicare',
  'Assisted Living',
  'Estate Planning',
  'Home Care',
];

export const CategoryBrowser = ({
  selectedCategory,
  onCategoryChange,
}: CategoryBrowserProps) => {
  const handleCategoryClick = (value: ResourceCategory | 'ALL') => {
    onCategoryChange(value);
  };

  return (
    <Card className="p-6 sticky top-24" data-testid="category-browser">
      {/* Categories Header */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-5 h-5 text-[#026857]" />
        <h3 className="text-lg text-gray-900 font-semibold">Categories</h3>
      </div>

      {/* Category List */}
      <div className="space-y-2" role="list" aria-label="Resource categories">
        {categories.map((category) => {
          const isSelected = category.value === selectedCategory;
          
          return (
            <button
              key={category.value}
              data-testid={`category-${category.value.toLowerCase()}`}
              onClick={() => handleCategoryClick(category.value)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                isSelected
                  ? 'bg-[#026857] text-white'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              role="listitem"
              aria-label={`Filter by ${category.label}`}
              aria-pressed={isSelected}
            >
              {category.label}
            </button>
          );
        })}
      </div>

      {/* Popular Topics */}
      <div className="mt-6 pt-6 border-t">
        <h4 className="text-sm text-gray-700 font-medium mb-3">Popular Topics</h4>
        <div className="flex flex-wrap gap-2">
          {popularTags.map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className="text-xs cursor-pointer hover:bg-gray-100"
            >
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      {/* Hidden span for tests to check selected category */}
      <span data-testid="selected-category" className="sr-only">
        {selectedCategory}
      </span>
    </Card>
  );
};

